"use client";

import { Select as BaseSelect } from "@base-ui/react/select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";

type SelectSize = "sm" | "default" | "lg";
type SelectVariant = "default" | "filter";

const SelectSizeContext = React.createContext<SelectSize>("default");

const useSelectSize = () => React.useContext(SelectSizeContext);

function Select({
  size = "default",
  ...props
}: React.ComponentProps<typeof BaseSelect.Root> & {
  size?: SelectSize;
}) {
  return (
    <SelectSizeContext.Provider value={size}>
      <BaseSelect.Root data-slot="select" {...props} />
    </SelectSizeContext.Provider>
  );
}

function SelectGroup({ ...props }: React.ComponentProps<typeof BaseSelect.Group>) {
  return <BaseSelect.Group data-slot="select-group" {...props} />;
}

/**
 * Displays the selected item label (or placeholder when empty).
 * Pass `placeholder` through to Base UI — do not return null for selected values.
 */
function SelectValue({ className, ...props }: React.ComponentProps<typeof BaseSelect.Value>) {
  return <BaseSelect.Value data-slot="select-value" className={cn(className)} {...props} />;
}

function SelectTrigger({
  className,
  size: sizeProp,
  variant = "default",
  children,
  ...props
}: React.ComponentProps<typeof BaseSelect.Trigger> & {
  size?: SelectSize;
  /** `filter` — compact gray table-toolbar control (e.g. "View by Role"). */
  variant?: SelectVariant;
}) {
  const ctxSize = useSelectSize();
  const size = sizeProp ?? ctxSize;
  const isFilter = variant === "filter";

  return (
    <BaseSelect.Trigger
      data-slot="select-trigger"
      data-size={size}
      data-variant={variant}
      className={cn(
        "flex w-full min-w-0 items-center justify-between gap-2 rounded-control outline-none transition-[color,box-shadow] disabled:cursor-not-allowed disabled:opacity-50 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:flex-1 *:data-[slot=select-value]:text-left [&_svg]:pointer-events-none [&_svg]:shrink-0",
        isFilter
          ? cn(
              "border-0 bg-gray-100 text-foreground shadow-none hover:bg-gray-200/70 focus-visible:ring-ring/40 focus-visible:ring-[3px]",
              "dark:bg-gray-100 dark:hover:bg-gray-300/80",
              // Match adjacent table search Input (h-9 / text-sm).
              "h-9 gap-1.5 px-3 text-sm [&_svg:not([class*='size-'])]:size-4",
            )
          : cn(
              "border-input data-placeholder:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 border bg-transparent shadow-xs focus-visible:ring-[3px] [&_svg:not([class*='size-'])]:size-4",
              size === "sm" && "h-8 px-3 py-1 text-sm",
              size === "default" && "h-9 px-3 py-1 text-sm",
              /** Matches Input `size="lg"` (h-11); text is 4px larger than default (14px → 18px). */
              size === "lg" && "h-11 bg-background px-4 py-2 text-[18px]",
            ),
        className,
      )}
      {...props}
    >
      {children}
      <BaseSelect.Icon className={cn("shrink-0", isFilter ? "text-muted-foreground" : "text-primary")}>
        <ChevronDownIcon className="size-4" />
      </BaseSelect.Icon>
    </BaseSelect.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = "popper",
  side = "bottom",
  align = "start",
  sideOffset = 4,
  collisionAvoidance,
  size: sizeProp,
  ...props
}: React.ComponentProps<typeof BaseSelect.Popup> & {
  position?: "popper" | string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  collisionAvoidance?: React.ComponentProps<typeof BaseSelect.Positioner>["collisionAvoidance"];
  size?: SelectSize;
}) {
  const ctxSize = useSelectSize();
  const size = sizeProp ?? ctxSize;

  // Same stacking fix as TooltipContent: when Select lives inside a Dialog portal,
  // Floating UI nests this portal under the dialog. Dialog's fixed + z-50 stacking
  // context then traps the select list, so z-index on the Popup alone is ignored.
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    setPortalContainer(document.body);
  }, []);

  return (
    <BaseSelect.Portal container={portalContainer}>
      {/* z-100 on Positioner — z-index only applies to positioned elements; Popup is relative/static. */}
      <BaseSelect.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionAvoidance={collisionAvoidance}
        // Default Base UI behavior scrolls the selected item to align with the
        // trigger, which leaves short lists mid-scroll and shows scroll arrows
        // over the first/last row. Standard anchored popovers are clearer here.
        alignItemWithTrigger={false}
        className="z-100"
      >
        <BaseSelect.Popup
          data-slot="select-content"
          data-size={size}
          className={cn(
            "bg-popover text-popover-foreground relative max-h-(--available-height) min-w-32 origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md outline-none",
            "transition-[transform,opacity] duration-150 data-starting-style:opacity-0 data-starting-style:scale-95 data-ending-style:opacity-0 data-ending-style:scale-95",
            size === "lg" ? "text-[16px]" : "text-sm",
            className,
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <BaseSelect.List
            className={cn(
              position === "popper" && "w-full min-w-(--anchor-width) scroll-my-1",
              size === "lg" ? "p-1.5" : "p-1",
            )}
          >
            {children}
          </BaseSelect.List>
          <SelectScrollDownButton />
        </BaseSelect.Popup>
      </BaseSelect.Positioner>
    </BaseSelect.Portal>
  );
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof BaseSelect.GroupLabel>) {
  const size = useSelectSize();
  return (
    <BaseSelect.GroupLabel
      data-slot="select-label"
      className={cn("text-muted-foreground px-2 py-1.5", size === "lg" ? "text-sm" : "text-xs", className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  endAdornment,
  ...props
}: React.ComponentProps<typeof BaseSelect.Item> & {
  /** Optional trailing content (e.g. color chip). Hidden while the checkmark shows. */
  endAdornment?: React.ReactNode;
}) {
  const size = useSelectSize();
  return (
    <BaseSelect.Item
      data-slot="select-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground group relative flex w-full cursor-default items-center gap-2 rounded-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-selected:bg-accent/70 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        size === "lg" ? "gap-2.5 py-2.5 pr-10 pl-3 text-[16px]" : "py-1.5 pr-8 pl-2 text-sm",
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          "absolute top-1/2 flex size-4 -translate-y-1/2 items-center justify-center",
          size === "lg" ? "right-3" : "right-2",
        )}
      >
        <BaseSelect.ItemIndicator>
          <CheckIcon className="text-foreground size-4 stroke-[2.5]" aria-hidden />
        </BaseSelect.ItemIndicator>
        {endAdornment ? (
          <span
            className="pointer-events-none absolute inset-0 flex items-center justify-center group-data-selected:invisible"
            aria-hidden
          >
            {endAdornment}
          </span>
        ) : null}
      </span>
      <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
    </BaseSelect.Item>
  );
}

function SelectSeparator({ className, ...props }: React.ComponentProps<typeof BaseSelect.Separator>) {
  return (
    <BaseSelect.Separator
      data-slot="select-separator"
      className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({ className, ...props }: React.ComponentProps<typeof BaseSelect.ScrollUpArrow>) {
  return (
    <BaseSelect.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(
        // Base UI sets position:absolute; pin full-width so the chevron stays a
        // top bar instead of collapsing onto the first item's label.
        "absolute top-0 left-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1",
        className,
      )}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </BaseSelect.ScrollUpArrow>
  );
}

function SelectScrollDownButton({ className, ...props }: React.ComponentProps<typeof BaseSelect.ScrollDownArrow>) {
  return (
    <BaseSelect.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(
        "absolute bottom-0 left-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1",
        className,
      )}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </BaseSelect.ScrollDownArrow>
  );
}

export type { SelectSize, SelectVariant };
export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
